import { useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { MapPin, Phone, Mail, Clock } from "lucide-react";
import {
  PHONE,
  PHONE_DISPLAY,
  EMAIL,
  GOOGLE_BUSINESS_URL,
} from "@/constants/contact";

function GoogleBusinessIcon({ size = 22 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
        fill="#4285F4"
      />
      <path
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        fill="#34A853"
      />
      <path
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        fill="#FBBC05"
      />
      <path
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        fill="#EA4335"
      />
    </svg>
  );
}

function WhatsAppIcon({ size = 22 }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="#fff"
      aria-hidden="true"
    >
      <path d="M.057 24l1.687-6.163a11.867 11.867 0 01-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 018.413 3.488 11.824 11.824 0 013.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 01-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 001.683 5.526l-.999 3.648 3.805-.873zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.247-.694.247-1.289.173-1.413z" />
    </svg>
  );
}
const SERVICES = [
  "Individual Therapy",
  "Couples Counseling",
  "Family Therapy",
  "Career Counseling",
  "Cognitive Behavioral Therapy",
  "Child Psychology",
  "Stress & Anxiety Management",
  "Other",
];

export default function Contact() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.15 });
  const [form, setForm] = useState({
    name: "",
    mobile: "",
    age: "",
    service: "",
    message: "",
  });
  const [error, setError] = useState("");

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = (e) => {
    e.preventDefault();
    const { name, mobile, age, service, message } = form;
    if (!name || !mobile || !age || !service || !message) {
      setError("Please fill in all fields before submitting.");
      return;
    }
    setError("");
    const text = `Hello Dr. Fiza! 🌿

I came across your website and would like to book a consultation.

👤 Name: ${name}
📱 Mobile: ${mobile}
🎂 Age: ${age}
🩺 Service Needed: ${service}
💬 Message: ${message}

Looking forward to hearing from you!`;
    window.open(
      `https://wa.me/${PHONE}?text=${encodeURIComponent(text)}`,
      "_blank",
    );
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      ref={ref}
      className="relative py-24 md:py-32 px-6"
      style={{
        background:
          "linear-gradient(135deg, #f7faf8 0%, #d4e8e0 60%, #b9d8c9 100%)",
      }}
    >
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="text-center mb-14"
        >
          <p
            className="font-body italic tracking-[0.3em] text-xs uppercase mb-4"
            style={{ color: "var(--color-accent)" }}
          >
            — Let's Connect —
          </p>
          <h2
            className="font-display font-bold"
            style={{
              fontSize: "clamp(2rem, 5vw, 3.25rem)",
              color: "var(--color-text-dark)",
            }}
            data-testid="contact-title"
          >
            GET IN TOUCH
          </h2>
          <div
            className="w-20 h-[3px] mx-auto mt-6 rounded-full"
            style={{ background: "var(--color-accent)" }}
          />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="grid lg:grid-cols-2 gap-10 lg:gap-16"
        >
          {/* Left: Info */}
          <div data-testid="contact-info">
            <h3
              className="font-display text-2xl md:text-3xl mb-8 leading-snug"
              style={{ color: "var(--color-text-dark)" }}
            >
              Your journey to wellness
              <span style={{ color: "var(--color-accent)" }}> starts here.</span>
            </h3>

            <div className="space-y-6">
              <div className="flex gap-4">
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ background: "var(--color-accent)" }}
                >
                  <MapPin size={20} color="#fff" />
                </div>
                <div>
                  <p
                    className="font-display uppercase tracking-widest text-sm mb-1"
                    style={{ color: "var(--color-accent)" }}
                  >
                    Address
                  </p>
                  <p
                    className="font-body"
                    style={{ color: "var(--color-text-dark)" }}
                  >
                    4/80, SFS, Mansarovar, Jaipur, Rajasthan 302020
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ background: "var(--color-accent)" }}
                >
                  <Phone size={20} color="#fff" />
                </div>
                <div>
                  <p
                    className="font-display uppercase tracking-widest text-sm mb-1"
                    style={{ color: "var(--color-accent)" }}
                  >
                    Phone / WhatsApp
                  </p>
                  <a
                    href={`https://wa.me/${PHONE}`}
                    target="_blank"
                    rel="noreferrer"
                    className="font-body hover:underline"
                    style={{ color: "var(--color-text-dark)" }}
                    data-testid="contact-phone-link"
                  >
                    {PHONE_DISPLAY}
                  </a>
                </div>
              </div>

              <div className="flex gap-4">
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ background: "var(--color-accent)" }}
                >
                  <Mail size={20} color="#fff" />
                </div>
                <div>
                  <p
                    className="font-display uppercase tracking-widest text-sm mb-1"
                    style={{ color: "var(--color-accent)" }}
                  >
                    Email
                  </p>
                  <a
                    href={`mailto:${EMAIL}`}
                    className="font-body hover:underline"
                    style={{ color: "var(--color-text-dark)" }}
                    data-testid="contact-email-link"
                  >
                    {EMAIL}
                  </a>
                </div>
              </div>

              <div className="flex gap-4">
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ background: "var(--color-accent)" }}
                >
                  <Clock size={20} color="#fff" />
                </div>
                <div>
                  <p
                    className="font-display uppercase tracking-widest text-sm mb-1"
                    style={{ color: "var(--color-accent)" }}
                  >
                    Hours
                  </p>
                  <p
                    className="font-body"
                    style={{ color: "var(--color-text-dark)" }}
                  >
                    Monday to Saturday · 10:00 AM – 7:00 PM IST
                  </p>
                </div>
              </div>
            </div>

            {/* Socials */}
            <div className="mt-10">
              <p
                className="font-display uppercase tracking-widest text-sm mb-4"
                style={{ color: "var(--color-accent)" }}
              >
                Follow & Connect
              </p>
              <div className="flex gap-4">
                <a
                  href={GOOGLE_BUSINESS_URL}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="social-google"
                  className="w-12 h-12 rounded-full flex items-center justify-center bg-white transition-transform hover:scale-110 shadow-sm"
                  style={{ border: "1px solid #e3efe9" }}
                  aria-label="Google Business Profile"
                >
                  <GoogleBusinessIcon size={22} />
                </a>
                <a
                  href={`https://wa.me/${PHONE}?text=Hello%20Dr.%20Fiza!%20I%20found%20your%20website%20and%20would%20like%20to%20book%20a%20consultation.`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="social-whatsapp"
                  className="w-12 h-12 rounded-full flex items-center justify-center transition-transform hover:scale-110"
                  style={{ background: "#25D366" }}
                  aria-label="WhatsApp"
                >
                  <WhatsAppIcon size={22} />
                </a>
              </div>
            </div>
          </div>

          {/* Right: Form */}
          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="rounded-2xl p-7 md:p-9 bg-white"
            style={{
              boxShadow: "0 18px 40px -20px rgba(31,51,41,0.2)",
              border: "1px solid #e3efe9",
            }}
          >
            <h3
              className="font-display text-2xl md:text-3xl mb-6"
              style={{ color: "var(--color-text-dark)" }}
            >
              Book a Consultation
            </h3>

            <div className="space-y-4">
              <input
                type="text"
                placeholder="Full Name"
                value={form.name}
                onChange={update("name")}
                className="form-input"
                data-testid="contact-input-name"
              />
              <input
                type="tel"
                placeholder="Mobile Number"
                value={form.mobile}
                onChange={update("mobile")}
                className="form-input"
                data-testid="contact-input-mobile"
              />
              <input
                type="number"
                placeholder="Age"
                value={form.age}
                onChange={update("age")}
                className="form-input"
                data-testid="contact-input-age"
              />
              <select
                value={form.service}
                onChange={update("service")}
                className="form-input"
                data-testid="contact-input-service"
              >
                <option value="">Service Needed</option>
                {SERVICES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
              <textarea
                rows={4}
                placeholder="Your Message"
                value={form.message}
                onChange={update("message")}
                className="form-input resize-none"
                data-testid="contact-input-message"
              />
            </div>

            {error && (
              <p
                className="mt-3 text-sm font-body"
                style={{ color: "#b94545" }}
                data-testid="contact-form-error"
              >
                {error}
              </p>
            )}

            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type="submit"
              data-testid="contact-submit-button"
              className="mt-6 w-full py-4 rounded-full font-display font-bold tracking-widest uppercase text-white text-sm md:text-base transition-colors"
              style={{ background: "var(--color-accent)" }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.background = "#4f8676")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.background = "var(--color-accent)")
              }
            >
              Send on WhatsApp
            </motion.button>
          </form>
        </motion.div>
      </div>
    </section>
  );
}
